import { CompositeChartProps as MantineCompositeChartProps } from '@mantine/charts';
import { useState, useCallback, useRef, useMemo } from 'react';

export type SeriesType = NonNullable<MantineCompositeChartProps['series']>[number];
export type ToggleSeriesType = SeriesType & { selected: boolean };

type ChartToggleParams = {
    /**
     * The initial series to display in the chart.
     */
    initialSeries: SeriesType[];
    /**
     * The type of series to use when toggling to cumulative view.
     * Defaults to 'line'.
     */
    cumulativeType?: SeriesType['type'];
    /**
     * If provided, this will be used to determine which series are initially selected.
     */
    defaultSelected?: string[];
};

export function useChartToggle({
    initialSeries,
    cumulativeType = 'line',
    defaultSelected,
}: ChartToggleParams) {
    const initialSeriesRef = useRef<SeriesType[]>(initialSeries);
    const initialCumulativeTypeRef = useRef<SeriesType['type']>(cumulativeType);

    const getInitialToggleSeries = (): ToggleSeriesType[] => {
        return initialSeriesRef.current.map(s => ({
            ...s,
            selected: defaultSelected ? defaultSelected.includes(s.name) : true,
        }));
    };

    const [toggleSeries, setToggleSeries] = useState<ToggleSeriesType[]>(getInitialToggleSeries());
    const [isCumulative, setIsCumulative] = useState(false);

    const graphSeries: SeriesType[] = useMemo(
        () =>
            toggleSeries.map(seriesItem => {
                if (!seriesItem.selected) {
                    return {
                        ...seriesItem,
                        color: 'transparent',
                        type: isCumulative ? initialCumulativeTypeRef.current || 'line' : seriesItem.type,
                    };
                }
                if (isCumulative) {
                    return {
                        ...seriesItem,
                        type: initialCumulativeTypeRef.current || 'line',
                        color: seriesItem.color || 'blue',
                    };
                }
                return { ...seriesItem };
            }),
        [toggleSeries, isCumulative]
    );

    const handleSeriesToggle = useCallback(({ checked, name }: { checked: boolean; name: string }) => {
        setToggleSeries(prev =>
            prev.map(seriesItem =>
                seriesItem.name === name ? { ...seriesItem, selected: checked } : seriesItem
            )
        );
    }, []);

    const handleCumulativeToggle = useCallback((checked: boolean) => {
        setIsCumulative(checked);
    }, []);

    return {
        graphSeries,
        toggleSeries,
        onSeriesToggle: handleSeriesToggle,
        onCumulativeToggle: handleCumulativeToggle,
    };
}
